<template>
    <v-dialog v-model="dialogInternal" persistent :max-width="$vuetify.breakpoint.mobile ? '95vw' : '1200'">
        <v-card elevation="0">
            <v-card-title class="d-flex align-center py-3 primary white--text">
                <span class="font-weight-bold">{{ getDialogTitle() }}</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog" color="white">
                    <v-icon style="color:white !important">mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pa-6">
                <!-- Custom Header -->
                <div class="d-flex mt-3 mb-2 px-4 justify-end align-center">
                    <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" outlined dense hide-details
                        :placeholder="$t('search')" clearable style="max-width: 200px; margin-right: auto;"
                        class="search-field"></v-text-field>
                    <div class="d-flex align-center">
                        <div class="body-2 mr-3" style="min-width: 90px; text-align: right;">
                            {{ from }} {{ $t('approvalFlow.cooperative.of') }} {{ filteredFarms.length }}
                        </div>
                        <v-btn class="mx-1" fab small outlined color="primary" :disabled="currentPage <= 1"
                            @click="pageChange(false)">
                            <v-icon dark>mdi-chevron-left</v-icon>
                        </v-btn>
                        <v-btn class="mx-1" fab small outlined color="primary" :disabled="currentPage >= totalPages"
                            @click="pageChange(true)">
                            <v-icon dark>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </div>

                <!-- Data Table -->
                <v-data-table :headers="headers" :items="paginatedFarms" hide-default-footer :loading="loading"
                    class="elevation-0 farm-changes-table" :items-per-page="-1">
                    <template v-if="paginatedFarms.length" v-slot:body="{ items }">
                        <tbody>
                            <tr v-for="(item, index) in items" :key="`row-${item.farmId || index}`"
                                style="position: relative;" @mouseover="selectItem(item)"
                                @mouseleave="selectItem(false)">
                                <td class="text-truncate">
                                    <div>
                                        <div class="font-weight-medium">{{ item.farmName || 'N/A' }}</div>
                                        <div class="caption text-muted">{{ item.farmId || 'N/A' }}</div>
                                    </div>
                                </td>
                                <td class="text-truncate">
                                    <div>
                                        <div class="font-weight-medium">{{ item.producerName || 'N/A' }}</div>
                                        <div class="caption text-muted">{{ item.producerEmail || '-' }}</div>
                                        <div class="caption text-muted">{{ item.producerMobile || '-' }}</div>
                                    </div>
                                </td>
                                <td class="text-truncate">
                                    {{ item.address || 'N/A' }}
                                </td>
                                <td class="text-truncate">
                                    <v-btn 
                                        v-if="getEUDRDeforestationStatus(item.status || item.deforestationStatus).text !== 'N/A'"
                                        small 
                                        rounded 
                                        min-width="150" 
                                        depressed 
                                        height="32"
                                        :class="[getEUDRDeforestationStatus(item.status || item.deforestationStatus).class, 'non-clickable']">
                                        {{ getEUDRDeforestationStatus(item.status || item.deforestationStatus).text }}
                                    </v-btn>
                                    <span v-else class="text--secondary">N/A</span>
                                </td>
                                <td>
                                    <div style="position: absolute; right: 10px; top: 10px;">
                                        <v-menu location="start">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon v-bind="attrs" v-on="on">
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense style="min-width: 150px;">
                                                <v-list-item @click="viewDetails(item)">
                                                    <v-list-item-title>{{ $t('view') }}</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item v-if="type === 'updated'" @click="toggleExpand(item)">
                                                    <v-list-item-title>{{ expandedFarm && expandedFarm.farmId ===
                                                        item.farmId ? $t('dueDiligence.hideDetails') :
                                                        $t('dueDiligence.showDetails') }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </td>
                            </tr>
                            <!-- Expanded Details for Updated Farms -->
                            <template v-if="type === 'updated' && expandedFarm">
                                <tr v-for="(item, index) in expandedFarmsList"
                                    :key="`expanded-${item.farmId || index}`">
                                    <td colspan="5" class="pa-4" style="background-color: #FAFAFA;">
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <v-card elevation="0" class="pa-3"
                                                    style="background-color: #FFFFFF; border-left: 4px solid #4CAF50;">
                                                    <h4 class="mb-2 font-weight-bold">{{
                                                        $t('dueDiligence.locationStatus') }}</h4>
                                                    <div class="d-flex align-center">
                                                        <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                                                        <span>{{ $t('dueDiligence.locationVerified') }}</span>
                                                    </div>
                                                </v-card>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-card elevation="0" class="pa-3"
                                                    style="background-color: #FFFFFF; border-left: 4px solid #2196F3;">
                                                    <h4 class="mb-2 font-weight-bold">{{
                                                        $t('deforestation.EUDRDeforestationStatus') }}</h4>
                                                    <div class="d-flex align-center mb-2">
                                                        <v-btn 
                                                            v-if="getEUDRDeforestationStatus(item.status || item.deforestationStatus).text !== 'N/A'"
                                                            small 
                                                            rounded 
                                                            min-width="150" 
                                                            depressed 
                                                            height="32"
                                                            :class="[getEUDRDeforestationStatus(item.status || item.deforestationStatus).class, 'non-clickable']">
                                                            {{ getEUDRDeforestationStatus(item.status || item.deforestationStatus).text }}
                                                        </v-btn>
                                                        <span v-else class="text--secondary">N/A</span>
                                                        <v-icon small color="info" class="ml-2">mdi-information</v-icon>
                                                    </div>
                                                    <div class="text-caption grey--text">
                                                        {{ $t('dueDiligence.dateOfIssue') }}: {{
                                                        formatDate(item.actionDate) }}
                                                    </div>
                                                </v-card>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-card elevation="0" class="pa-3"
                                                    style="background-color: #FFFFFF; border-left: 4px solid #FF9800;">
                                                    <h4 class="mb-2 font-weight-bold">{{ $t('dueDiligence.assessments')
                                                        }}</h4>
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="mr-2">{{ $t('dueDiligence.riskAssessment') }}: {{
                                                            getRiskAssessmentStatus(item) }}</span>
                                                        <v-icon small color="info">mdi-information</v-icon>
                                                    </div>
                                                    <div class="text-caption grey--text">
                                                        {{ $t('dueDiligence.dateOfSubmission') }}: {{
                                                        formatDate(item.actionDate) }}
                                                    </div>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </template>
                    <template v-slot:footer></template>
                </v-data-table>
            </v-card-text>
            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn depressed color="primary" @click="closeDialog">
                    {{ $t('close') }}
                </v-btn>
            </v-card-actions>
        </v-card>


    </v-dialog>
</template>

<script>
export default {
    name: 'FarmChangesTable',
    props: {
        dialog: {
            type: Boolean,
            default: false
        },
        farms: {
            type: Array,
            default: () => []
        },
        type: {
            type: String,
            default: 'removed' // 'removed', 'updated', 'added'
        },
        diligenceId: {
            type: [Number, String],
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            search: '',
            currentPage: 1,
            itemsPerPage: 10,
            expandedFarm: null,
            selectedItem: false,
            headers: [
                {
                    text: this.$t('dueDiligence.productionPlaceAndFarmID'),
                    value: 'farmName',
                    align: 'start',
                    sortable: false,
                    class: 'black--text'
                },
                {
                    text: this.$t('dueDiligence.producerName'),
                    value: 'producerName',
                    align: 'start',
                    sortable: false,
                    class: 'black--text'
                },
                {
                    text: this.$t('dueDiligence.addressOfProduction'),
                    value: 'address',
                    align: 'start',
                    sortable: false,
                    class: 'black--text'
                },
                {
                    text: this.$t('dueDiligence.status'),
                    value: 'status',
                    align: 'start',
                    sortable: false,
                    class: 'black--text'
                },
                {
                    text: this.$t('approvalFlow.cooperative.actions'),
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    class: 'black--text'
                }
            ]
        };
    },
    computed: {
        dialogInternal: {
            get() {
                return this.dialog;
            },
            set(value) {
                if (!value) {
                    this.$emit('close');
                }
            }
        },
        filteredFarms() {
            if (!this.search) return this.farms;
            const searchTerm = this.search.toLowerCase();
            return this.farms.filter(farm =>
                (farm.farmName || '').toLowerCase().includes(searchTerm) ||
                (farm.farmId?.toString() || '').includes(searchTerm) ||
                (farm.producerName || '').toLowerCase().includes(searchTerm) ||
                (farm.address || '').toLowerCase().includes(searchTerm)
            );
        },
        paginatedFarms() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredFarms.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredFarms.length / this.itemsPerPage);
        },
        from() {
            return this.filteredFarms.length > 0
                ? (this.currentPage - 1) * this.itemsPerPage + 1
                : 0;
        },
        to() {
            const to = this.currentPage * this.itemsPerPage;
            return to > this.filteredFarms.length ? this.filteredFarms.length : to;
        },
        expandedFarmsList() {
            if (this.type !== 'updated' || !this.expandedFarm) {
                return [];
            }
            return this.paginatedFarms.filter(item =>
                this.expandedFarm && this.expandedFarm.farmId === item.farmId
            );
        }
    },
    watch: {
        farms: {
            handler() {
                this.currentPage = 1;
                this.search = '';
                this.expandedFarm = null;
            },
            immediate: true
        },
        dialog(newVal) {
            if (newVal) {
                this.search = '';
                this.currentPage = 1;
                this.expandedFarm = null;
            }
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },
        getStatusClass() {
            if (this.type === 'removed') {
                return 'red-button';
            } else if (this.type === 'added') {
                return 'green-button';
            } else if (this.type === 'updated') {
                return 'green-button';
            }
            return 'gray-button';
        },
        getStatusText() {
            if (this.type === 'removed') {
                return this.$t('deforestation.nonCompliant');
            } else if (this.type === 'added') {
                return this.$t('deforestation.compliant');
            } else if (this.type === 'updated') {
                return this.$t('deforestation.updated');
            }
            return this.$t('deforestation.unknown');
        },
        getEUDRDeforestationStatus(status) {
            if (!status) {
                return { class: '', text: 'N/A' };
            }
            switch (status) {
                case 'Compliant':
                    return { class: 'green-button', text: this.$t("deforestation.compliant") };
                case 'Non-compliant':
                    return { class: 'red-button', text: this.$t("deforestation.nonCompliant") };
                case 'Zero/Negligible Deforestation Probability':
                case 'Zero/Negligible Probability':
                    return { class: 'green-button', text: this.$t("deforestation.zeroOrNegligibleProbability") };
                case 'Very High Deforestation Probability':
                case 'Very High Probability':
                    return { class: 'very-high-button', text: this.$t("deforestation.veryHighProbability") };
                case 'Very Low Deforestation Probability':
                case 'Very Low Probability':
                    return { class: 'very-low-button', text: this.$t("deforestation.veryLowProbability") };
                case 'High Deforestation Probability':
                case 'High Probability':
                    return { class: 'red-button', text: this.$t("deforestation.highProbability") };
                case 'Low Deforestation Probability':
                case 'Low Probability':
                    return { class: 'orange-button', text: this.$t("deforestation.lowProbability") };
                case 'Medium Deforestation Probability':
                case 'Medium Probability':
                    return { class: 'medium-button', text: this.$t("deforestation.mediumProbability") };
                case 'Manually Deforestation Mitigated':
                case 'Manually Mitigated':
                    return { class: 'manually-button', text: this.$t("dueDiligence.manualMitigated") };
                case 'unknown_error':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.unknown_error") };
                case 'geo_location_error':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.geo_location_error") };
                case 'region_not_available':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.region_not_available") };
                case 'area_too_large':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.area_too_large") };
                case 'area_too_small':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.area_too_small") };
                case 'invalid_request_data':
                    return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.invalid_request_data") };
                default:
                    return { class: '', text: 'N/A' };
            }
        },
        getDeforestationStatus(farm) {
            const status = farm.status || farm.eudrDeforestationStatus || farm.deforestationStatus;
            if (status) {
                return this.getEUDRDeforestationStatus(status).text;
            }
            return this.$t('deforestation.zeroOrNegligibleProbability');
        },
        getRiskAssessmentStatus(farm) {
            if (farm.riskAssessmentStatus) {
                return farm.riskAssessmentStatus === 'Approved'
                    ? this.$t('dueDiligence.approved')
                    : farm.riskAssessmentStatus;
            }
            return this.$t('dueDiligence.approved');
        },
        pageChange(next) {
            if (next) {
                this.currentPage++;
            } else {
                this.currentPage--;
            }
        },
        toggleExpand(farm) {
            if (this.type === 'updated') {
                if (this.expandedFarm && this.expandedFarm.farmId === farm.farmId) {
                    this.expandedFarm = null;
                } else {
                    this.expandedFarm = farm;
                }
            }
        },
        selectItem(item) {
            this.selectedItem = item;
        },
        viewDetails(item) {
            this.$emit('view-details', item);
        },
        closeDialog() {
            this.$emit('close');
        },
        getDialogTitle() {
            const titleMap = {
                'removed': this.$t('dueDiligence.farmsRemoved'),
                'updated': this.$t('dueDiligence.farmsUpdated'),
                'added': this.$t('dueDiligence.farmsAdded')
            };
            return titleMap[this.type] || this.$t('dueDiligence.farmChanges');
        }
    }
};
</script>

<style lang="scss" scoped>
.farm-changes-table {
    ::v-deep .v-data-table__wrapper {
        max-height: 600px;
        overflow-y: auto;
    }

    ::v-deep .v-data-table-header span {
        text-transform: uppercase !important;
        color: black;
        white-space: nowrap !important;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    ::v-deep .v-data-table-header th {
        white-space: nowrap !important;
        min-width: fit-content;
    }
}

.red-button {
    background-color: #ffe6e6 !important;
    border: 2px solid #d32f2f !important;
    color: #d32f2f !important;
}

.green-button {
    background-color: #e8f5e8 !important;
    border: 2px solid #2e7d32 !important;
    color: #2e7d32 !important;
}

.gray-button {
    background-color: #e8e8e8 !important;
    border: 2px solid #000000 !important;
    color: #000000 !important;
}

.orange-button {
    background-color: #fff7ec !important;
    border: 2px solid #ffa826 !important;
    color: #ffa826 !important;
}

.very-high-button {
    background-color: #ffebee !important;
    border: 2px solid #c62828 !important;
    color: #c62828 !important;
}

.very-low-button {
    background-color: #e8f5e8 !important;
    border: 2px solid #2e7d32 !important;
    color: #2e7d32 !important;
}

.medium-button {
    background-color: #fff3e0 !important;
    border: 2px solid #f57c00 !important;
    color: #f57c00 !important;
}

.manually-button {
    background-color: #e3f2fd !important;
    border: 2px solid #1976d2 !important;
    color: #1976d2 !important;
}

.deep-orange-darken-1-button {
    background-color: #fff3e0 !important;
    border: 2px solid #ff6f00 !important;
    color: #ff6f00 !important;
}

.non-clickable {
    pointer-events: none;
    cursor: default;
}
</style>
